import type { DocumentPipelineFilter } from '@kanbu/schema/contracts';
import { WebsitePageStatus, type SourceType } from '@kanbu/schema/enums';
import { useLingui } from '@lingui/react/macro';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { getQueryKey } from '@trpc/react-query';
import { toast } from '@utima/ui';
import { useCallback, useMemo, useState } from 'react';

import { useDocumentMutation } from '@/features/documents/hooks/useDocumentMutation';
import type { ExportDocument } from '@/features/documents/lib/exportDocuments';
import { mergeContents } from '@/features/knowledge-base/components/KBContentTable';
import type {
  KBFolderDeletePrompt,
  KBFolderDeleteTarget,
} from '@/features/knowledge-base/components/kbTypes';
import type { KBContentColumnActions } from '@/features/knowledge-base/hooks/useKBContentColumns';
import type { useKBMutations } from '@/features/knowledge-base/hooks/useKBMutations';
import type { useKBSelection } from '@/features/knowledge-base/hooks/useKBSelection';
import { trpc, trpcClient } from '@/services/trpc';

import {
  type KBWebsitePageFilter,
  toFindContentsPageFilter,
} from '../lib/pageFilter';

const KB_PAGE_SIZE = 50;

export interface UseKBContentsOptions {
  folderId: string | null;
  chatId?: string;
  search: string;
  statusFilter?: KBWebsitePageFilter;
  documentPipelineFilter?: DocumentPipelineFilter;
  sourceTypeFilter?: SourceType;
  selection: ReturnType<typeof useKBSelection>;
  mutations: ReturnType<typeof useKBMutations>;
  onFolderSettings?: (
    folderId: string,
    tab?: 'general' | 'auto-update' | 'agents',
  ) => void;
}

export function useKBContents({
  folderId,
  chatId,
  search,
  statusFilter,
  documentPipelineFilter,
  sourceTypeFilter,
  selection,
  mutations,
  onFolderSettings,
}: UseKBContentsOptions) {
  const { t } = useLingui();
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const [exportSelection, setExportSelection] = useState<ExportDocument[]>([]);
  const handleCloseExport = useCallback(() => setExportSelection([]), []);

  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(
    null,
  );
  const [defaultTab, setDefaultTab] = useState<string | undefined>();
  const [isBulkAgentAccessOpen, setIsBulkAgentAccessOpen] = useState(false);
  const [isBulkAutoUpdateOpen, setIsBulkAutoUpdateOpen] = useState(false);
  const [isBulkWeightOpen, setIsBulkWeightOpen] = useState(false);
  const [folderDeletePrompt, setFolderDeletePrompt] =
    useState<KBFolderDeletePrompt | null>(null);

  const [contentUpdatedSort, setContentUpdatedSort] = useState<
    'asc' | 'desc'
  >();
  const pageFilter = toFindContentsPageFilter(statusFilter);

  const queryInput = useMemo(
    () => ({
      folderId,
      search: search || undefined,
      status: pageFilter.status,
      pageHasDocument: pageFilter.pageHasDocument,
      chatId,
      documentPipelineFilter,
      sourceType: sourceTypeFilter,
      contentUpdatedSort,
    }),
    [
      folderId,
      search,
      pageFilter.status,
      pageFilter.pageHasDocument,
      chatId,
      documentPipelineFilter,
      sourceTypeFilter,
      contentUpdatedSort,
    ],
  );

  const {
    data: contentsData,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [
      ...getQueryKey(trpc.documentFolders.findContents, queryInput),
      'infinite',
    ],
    queryFn: ({ pageParam = 1 }) =>
      trpcClient.documentFolders.findContents.query({
        ...queryInput,
        pagination: { page: pageParam, pageSize: KB_PAGE_SIZE },
      }),
    getNextPageParam: lastPage =>
      lastPage.meta?.hasMore ? lastPage.meta.page + 1 : undefined,
    initialPageParam: 1,
    refetchOnMount: 'always',
    refetchInterval: query =>
      query.state.data?.pages.some(page =>
        page.documents.some(doc =>
          [
            'uploading',
            'ready',
            'parsing',
            'parsingAsync',
            'parsed',
            'embedding',
          ].includes(doc.status),
        ),
      )
        ? 5000
        : false,
  });

  const rows = useMemo(() => {
    if (!contentsData?.pages.length) return [];

    const mergedRows = contentsData.pages.flatMap(page =>
      mergeContents(page, contentUpdatedSort),
    );

    if (!contentUpdatedSort) return mergedRows;

    const groupOrder = { folder: 0, document: 1, page: 2 };
    const uniqueRows = [
      ...new Map(mergedRows.map(row => [row.id, row])).values(),
    ];

    return uniqueRows.sort(
      (a, b) => groupOrder[a.rowType] - groupOrder[b.rowType],
    );
  }, [contentsData, contentUpdatedSort]);

  const resolveFolderDeleteTarget = useCallback(
    (targetFolderId: string): KBFolderDeleteTarget => {
      const row = rows.find(
        item => item.rowType === 'folder' && item.id === targetFolderId,
      );

      return {
        id: targetFolderId,
        name: row?.rowType === 'folder' ? row.data.name : targetFolderId,
      };
    },
    [rows],
  );

  const requestDeleteFolder = useCallback(
    (
      targetFolderId: string,
      options?: { onDeleted?: () => void | Promise<void> },
    ) => {
      setFolderDeletePrompt({
        mode: 'single',
        folder: resolveFolderDeleteTarget(targetFolderId),
        onDeleted:
          options?.onDeleted ??
          (() =>
            utils.documentFolders.findContents.invalidate({
              folderId,
              chatId,
            })),
      });
    },
    [resolveFolderDeleteTarget, utils, folderId, chatId],
  );

  const requestDeleteFolders = useCallback(
    (folderIds: string[]) => {
      if (folderIds.length === 0) {
        return;
      }

      if (folderIds.length === 1) {
        requestDeleteFolder(folderIds[0]!);

        return;
      }

      setFolderDeletePrompt({
        mode: 'bulk',
        folders: folderIds.map(id => resolveFolderDeleteTarget(id)),
        onDeleted: () =>
          utils.documentFolders.findContents.invalidate({ folderId, chatId }),
      });
    },
    [requestDeleteFolder, resolveFolderDeleteTarget, utils, folderId, chatId],
  );

  const clearFolderDeletePrompt = useCallback(() => {
    setFolderDeletePrompt(null);
  }, []);

  const firstPage = contentsData?.pages[0];

  const pageCounts = useMemo(() => {
    const serverCounts = firstPage?.pageStatusCounts;

    if (!serverCounts) return null;

    return {
      new: serverCounts.New ?? 0,
      removed: serverCounts.Removed ?? 0,
      existing: serverCounts.Existing ?? 0,
      untracked: serverCounts.Untracked ?? 0,
    };
  }, [firstPage]);

  const handleOpenDocumentTab = useCallback(
    (documentId: string, tab: string) => {
      setDefaultTab(tab);
      setSelectedDocumentId(documentId);
    },
    [],
  );

  const handleDeleteDocuments = useCallback(
    async (documentIds: string[]) => {
      if (documentIds.length === 0) {
        return;
      }

      try {
        await mutations.bulkDelete.mutateAsync({ documentIds });
        toast.success(
          documentIds.length === 1 ? t`Document deleted` : t`Documents deleted`,
        );
      } catch {
        toast.error(t`Failed to delete documents`);
      }
    },
    [mutations.bulkDelete, t],
  );

  const handleDeleteRemovedPages = useCallback(
    async (pageIds: string[]) => {
      try {
        await mutations.deleteRemovedPages.mutateAsync({ pageIds });
        toast.success(t`Pages deleted`);
      } catch {
        toast.error(t`Failed to delete pages`);
      }
    },
    [mutations.deleteRemovedPages, t],
  );

  const handleDeleteDocument = useCallback(
    async (documentId: string) => {
      const row = rows.find(
        row => row.rowType === 'document' && row.id === documentId,
      );

      if (
        row?.rowType === 'document' &&
        row.data.websitePage?.status === WebsitePageStatus.Removed
      ) {
        await handleDeleteRemovedPages([row.data.websitePage.id]);

        return;
      }

      await handleDeleteDocuments([documentId]);
    },
    [handleDeleteDocuments, handleDeleteRemovedPages, rows],
  );

  const handleExcludeFromKB = useCallback(
    async (target: {
      pageIds?: string[];
      documentIds?: string[];
      folderIds?: string[];
    }) => {
      try {
        await mutations.excludePages.mutateAsync({
          pageIds: target.pageIds ?? [],
          documentIds: target.documentIds ?? [],
          folderIds: target.folderIds ?? [],
        });
        toast.success(t`Removed from tracked pages`);
      } catch {
        toast.error(t`Failed to remove from tracked pages`);
      }
    },
    [mutations.excludePages, t],
  );

  const handleDismissPage = useCallback(
    async (pageId: string) => {
      try {
        await mutations.dismissPages.mutateAsync({ pageIds: [pageId] });
        toast.success(t`Pages dismissed`);
      } catch {
        toast.error(t`Failed to dismiss pages`);
      }
    },
    [mutations.dismissPages, t],
  );

  const handleIncludePage = useCallback(
    async (pageId: string) => {
      try {
        await mutations.includePages.mutateAsync({ pageIds: [pageId] });
        toast.success(t`Pages added to Knowledge Base`);
      } catch {
        toast.error(t`Failed to add pages`);
      }
    },
    [mutations.includePages, t],
  );

  const handleSelectDocument = useCallback((id: string | null) => {
    if (id === null) {
      setDefaultTab(undefined);
    }

    setSelectedDocumentId(id);
  }, []);

  const selectedSettingRows = useMemo(
    () =>
      rows.filter(
        row =>
          selection.selectedItems.has(row.id) &&
          (row.rowType === 'document' || row.rowType === 'folder'),
      ),
    [rows, selection.selectedItems],
  );

  const handleBulkAgentAccess = useCallback(() => {
    if (selection.selectedItems.size === 1) {
      const firstSelected = selection.selectedItems.entries().next().value as
        | [string, string]
        | undefined;

      if (firstSelected?.[1] === 'document') {
        handleOpenDocumentTab(firstSelected[0], 'agents');

        return;
      }

      if (firstSelected?.[1] === 'folder') {
        onFolderSettings?.(firstSelected[0], 'agents');

        return;
      }
    }

    setIsBulkAgentAccessOpen(true);
  }, [handleOpenDocumentTab, onFolderSettings, selection.selectedItems]);

  const handleBulkAutoUpdate = useCallback(() => {
    if (selection.selectedItems.size === 1) {
      const firstSelected = selection.selectedItems.entries().next().value as
        | [string, string]
        | undefined;

      if (firstSelected?.[1] === 'document') {
        handleOpenDocumentTab(firstSelected[0], 'auto-update');

        return;
      }

      if (firstSelected?.[1] === 'folder') {
        onFolderSettings?.(firstSelected[0], 'auto-update');

        return;
      }
    }

    setIsBulkAutoUpdateOpen(true);
  }, [handleOpenDocumentTab, onFolderSettings, selection.selectedItems]);

  const handleBulkWeight = useCallback(() => {
    setIsBulkWeightOpen(true);
  }, []);

  const handleUpdateDocumentPriority = useCallback(
    async (documentId: string, weight: number) => {
      await mutations.bulkWeight.mutateAsync({
        documentIds: [documentId],
        weight,
      });
      toast.success(t`Priority updated`);
    },
    [mutations.bulkWeight, t],
  );

  const handleUpdateFolderPriority = useCallback(
    async (folderId: string, weight: number) => {
      await mutations.updateFolder.mutateAsync({
        id: folderId,
        defaultWeight: weight,
      });
      toast.success(t`Priority updated`);
    },
    [mutations.updateFolder, t],
  );

  const docMutations = useDocumentMutation();

  const handleRefreshDocument = useCallback(
    async (documentId: string) => {
      try {
        await docMutations.refresh.mutateAsync({ id: documentId });
        toast.success(t`Document refresh started`);
      } catch {
        toast.error(t`Failed to refresh document`);
      }
    },
    [docMutations.refresh, t],
  );

  const handleResetEmbedding = useCallback(
    async (documentId: string) => {
      try {
        await docMutations.resetEmbedding.mutateAsync({ id: documentId });
        toast.success(t`Embeddings reset started`);
      } catch {
        toast.error(t`Failed to reset embeddings`);
      }
    },
    [docMutations.resetEmbedding, t],
  );

  const [testCasesDocumentId, setTestCasesDocumentId] = useState<string | null>(
    null,
  );

  const handleGenerateTestCases = useCallback((documentId: string) => {
    setTestCasesDocumentId(documentId);
  }, []);

  const handleNavigateToFolder = useCallback(
    (targetFolderId: string) => {
      navigate({ to: '/knowledge-base', search: { folderId: targetFolderId } });
    },
    [navigate],
  );

  const handleOpenExport = useCallback(
    (documentIds: string[]) => {
      const names = new Map(
        rows.flatMap(row =>
          row.rowType === 'document' ? [[row.id, row.data.name] as const] : [],
        ),
      );
      setExportSelection(documentIds.map(id => ({ id, name: names.get(id) })));
    },
    [rows],
  );

  const columnActions = useMemo<KBContentColumnActions>(
    () => ({
      onOpenDocumentTab: handleOpenDocumentTab,
      onViewDetails: handleSelectDocument,
      onExportDocuments: handleOpenExport,
      onDeleteDocument: handleDeleteDocument,
      onDeleteRemovedPages: handleDeleteRemovedPages,
      onRequestDeleteFolder: requestDeleteFolder,
      onExcludeFromKB: handleExcludeFromKB,
      onFolderSettings: onFolderSettings ?? (() => {}),
      onDismissPage: handleDismissPage,
      onIncludePage: handleIncludePage,
      onRefreshDocument: handleRefreshDocument,
      onResetEmbedding: handleResetEmbedding,
      onGenerateTestCases: handleGenerateTestCases,
      onNavigateToFolder: handleNavigateToFolder,
      onUpdateDocumentPriority: handleUpdateDocumentPriority,
      onUpdateFolderPriority: handleUpdateFolderPriority,
    }),
    [
      handleOpenDocumentTab,
      handleSelectDocument,
      handleOpenExport,
      handleDeleteDocument,
      handleDeleteRemovedPages,
      requestDeleteFolder,
      handleExcludeFromKB,
      onFolderSettings,
      handleDismissPage,
      handleIncludePage,
      handleRefreshDocument,
      handleResetEmbedding,
      handleGenerateTestCases,
      handleNavigateToFolder,
      handleUpdateDocumentPriority,
      handleUpdateFolderPriority,
    ],
  );

  const folderPaths = useMemo(() => {
    if (!contentsData?.pages.length) return null;

    const merged: Record<string, string> = {};

    for (const page of contentsData.pages) {
      if (page.folderPaths) {
        Object.assign(merged, page.folderPaths);
      }
    }

    return Object.keys(merged).length > 0 ? merged : null;
  }, [contentsData]);

  return {
    exportSelection,
    handleOpenExport,
    handleCloseExport,
    rows,
    contentUpdatedSort,
    setContentUpdatedSort,
    pageCounts,
    folderPaths,
    isLoading,
    contentsData: firstPage,
    fetchNextPage,
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    selectedDocumentId,
    setSelectedDocumentId,
    defaultTab,
    setDefaultTab,
    selectedSettingRows,
    isBulkAgentAccessOpen,
    setIsBulkAgentAccessOpen,
    isBulkAutoUpdateOpen,
    setIsBulkAutoUpdateOpen,
    isBulkWeightOpen,
    setIsBulkWeightOpen,
    handleOpenDocumentTab,
    handleDeleteDocuments,
    handleDeleteDocument,
    handleExcludeFromKB,
    folderDeletePrompt,
    clearFolderDeletePrompt,
    requestDeleteFolder,
    requestDeleteFolders,
    handleSelectDocument,
    handleBulkAgentAccess,
    handleBulkAutoUpdate,
    handleBulkWeight,
    columnActions,
    testCasesDocumentId,
    setTestCasesDocumentId,
  };
}
